import {Location} from '@angular/common';
import {Inject, Injectable, NgZone} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {LocalStorage} from '@ngx-pwa/local-storage';
import {SptSystemInformation} from '@spout/any-shared/models';
import {
  accountLoadError,
  accountUpdated,
  addSubscriptionIdToAccount,
  addSubscriptionItemToAccount,
  addSubscriptionToAccount,
  addTrialToAccount,
  loadPromoCode,
  upsertWebsocketRegistry
} from '@spout/web-global/actions';
import {
  firestoreGetMusicianNotificationsPath,
  firestorePromoCodeDict,
  firestoreTrial,
  getMusicianAccountDevicesPath,
  getMusicianAccountPath,
  getMusicianAccountPathByUID,
  getMusicianPublicPathByUser,
  getMusicianSubscriptionPath
} from '@spout/web-global/fns';
import {
  ACCOUNT_FEATURE_KEY,
  AccountState,
  AggregateFirebaseSnapshotChanges,
  ApiNotifiation,
  APP_ROUTES,
  AuthAccountStates,
  AuthAccountStatesConnect,
  AuthState,
  CurrentSubscription,
  ENVIRONMENT,
  getRouteConfigLinkByName,
  IEnvironmentState,
  LOGOUT_REDIRECT,
  NotificationTypes,
  Price,
  Product,
  ProductFirestoreSubscription,
  PromoCodeDict,
  PublicProfile,
  SptFirebaseConnectionService,
  SubscriptionItem,
  TrialModel
} from '@spout/web-global/models';
import {deviceIsElectron, selectAuthAccountConnect$, selectAuthState} from '@spout/web-global/selectors';
import {clone} from '@uiux/fn';
import {
  DocumentChange,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  getDoc,
  onSnapshot,
  QueryDocumentSnapshot,
  QuerySnapshot
} from 'firebase/firestore';

import {Observable, of, Subscription, timer} from 'rxjs';
import {catchError, distinctUntilKeyChanged, filter, map, mergeMap, switchMap, take, tap} from 'rxjs/operators';
import {Exists, removeTimestampCTorFromDocumentSnapshot, SptFirestoreService} from '../firestore';
import {CreateDefaultEntitiesService} from './create-default-entities.service';
import {TrackCloudStorageService} from './track-cloud-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AccountService implements SptFirebaseConnectionService<AccountState> {
  private watchLoginSub: Subscription = Subscription.EMPTY;
  private accountChangesSub: (() => void) | undefined;
  private notificationsSub: (() => void) | undefined;
  private subscriptionsSub: (() => void) | undefined;
  private promoCodeSub: (() => void) | undefined;
  private trialSub: (() => void) | undefined;
  private isCreatingDefaultEntities = false;

  constructor(
    private store: Store<AuthAccountStatesConnect>,
    private sptFirestore: SptFirestoreService,
    private route: ActivatedRoute,
    private location: Location,
    private router: Router,
    private zone: NgZone,
    private locationService: Location,
    private storage: LocalStorage,
    public dialog: MatDialog,
    private defaultIdsService: CreateDefaultEntitiesService,
    private trackCloudStorage: TrackCloudStorageService,
    @Inject(ENVIRONMENT) private environment: IEnvironmentState,
    @Inject(LOGOUT_REDIRECT) private logoutRedirect: string
  ) {
    // console.log('Account Service');

    const that = this;

    this.zone.run(() => {
      that.store.dispatch(
        upsertWebsocketRegistry({
          id: ACCOUNT_FEATURE_KEY
        })
      );
    });

    this.store.pipe(selectAuthAccountConnect$).subscribe((s: AuthAccountStatesConnect) => {
      if (s.doConnect) {
        this.onConnect.call(this, s.user);
      } else {
        this.onDisconnect.call(this);
      }
    });
  }

  createAccountIfNotExist(auth: AuthState): Observable<any> {
    const accountPayload: AccountState = {
      active: true,
      production: this.environment.production,

      defaultProjectId: null,
      defaultSongId: null,
      defaultTrackId: null,
      defaultMixId: null,

      displayName: auth.displayName,
      email: auth.email,
      emailVerified: auth.emailVerified,
      phoneNumber: auth.phoneNumber,
      photoURL: auth.photoURL,
      providerId: auth.providerId,
      stageName: null,
      uid: auth.uid,

      hasGenres: null,
      hasInstruments: null,

      monitorStore: false,
      monitorMaxAge: 50,

      createdAt: {
        nanoseconds: null,
        seconds: null
      },
      updatedAt: {
        nanoseconds: null,
        seconds: null
      }
    };

    return this.sptFirestore
      .setDocIfNotExist<AccountState>(getMusicianAccountPathByUID(<string>auth.uid), accountPayload)
      .pipe(
        switchMap((d: Exists<AccountState>) => {
          if (d.data && d.data.uid && d.data.uid.length > 0) {
            return of(d);
          }

          /**
           * Upon account creation, stripe will automatically add subscription
           * info to the account object. This is to upsert auth account
           * info to the same object.
           */
          return this.sptFirestore.upsertDoc<AccountState>(
            getMusicianAccountPathByUID(<string>auth.uid),
            accountPayload
          );
          // .pipe(
          //   tap((d: Exists<AccountState>) => {
          //     console.log(d);
          //   })
          // );
        }),

        catchError((error: any) => {
          console.error(error);
          return of(accountLoadError(error));
          // TODO handle error notifications
        })
      );
  }

  saveMusicianDeviceSystemInformation(
    auth: AuthState,
    desktopInformation: SptSystemInformation,
    serial: string
  ): Observable<Exists<AccountState | any>> {
    return this.sptFirestore
      .setDocIfNotExist(getMusicianAccountDevicesPath({auth} as AuthAccountStates, serial), desktopInformation)
      .pipe(
        catchError((error: any) => {
          console.error(error);
          return of(<Exists<AccountState | any>>{
            data: null,
            exists: false
          });
        })
      );
  }

  saveToFirebase(account: Partial<AccountState>) {
    // console.log(account);

    this.store
      .pipe(
        selectAuthAccountConnect$,
        filter((d: AuthAccountStatesConnect) => d.doConnect),
        take(1),
        switchMap((d: AuthAccountStatesConnect) => {
          // console.log(d);

          let payload: AccountState = clone(d.user.account);

          payload = Object.assign(payload, account);

          return this.sptFirestore.update<AccountState>(getMusicianAccountPath(d.user), payload);
        })
      )
      .subscribe((r: Exists<AccountState>) => {
        /* noop */
      });
  }

  saveBetaTestCode(account: Partial<AccountState>) {
    this.store
      .pipe(
        selectAuthAccountConnect$,
        filter((d: AuthAccountStatesConnect) => d.doConnect),
        take(1),
        switchMap((d: AuthAccountStatesConnect) => {
          // console.log(d);

          let payload: AccountState = clone(d.user.account);

          payload = Object.assign(payload, account);

          // console.log(payload);

          return this.sptFirestore.update<AccountState>(getMusicianAccountPath(d.user), payload);
        })
      )
      .subscribe((r: Exists<AccountState>) => {
        /* noop */
      });
  }

  onConnect(user: AuthAccountStates): void {
    const that = this;

    if (user && user.auth && user.auth.uid) {
      this.trackCloudStorage.connect(user);

      /**
       * SUBSCRIPTION
       */
      if (this.subscriptionsSub) {
        this.subscriptionsSub();
      }

      this.subscriptionsSub = onSnapshot(
        this.sptFirestore.collectionRef(getMusicianSubscriptionPath(user.auth.uid)),
        (_docs: QuerySnapshot<DocumentData>) => {
          that.zone.run(() => {
            _docs.forEach(async (_doc: QueryDocumentSnapshot<DocumentData>) => {
              that.store.dispatch(
                addSubscriptionIdToAccount({
                  subscriptionId: _doc.id
                })
              );

              const firestoreSubscription: ProductFirestoreSubscription = <ProductFirestoreSubscription>_doc.data();
              const subscription: CurrentSubscription = {
                ...(<any>firestoreSubscription)
              };

              subscription.product = <Product>(await getDoc(firestoreSubscription.product)).data();
              subscription.price = <Price>(await getDoc(firestoreSubscription.product)).data();
              // subscription.product = removeTimestampCTorFromDocumentSnapshot<Product>(
              //   await firestoreSubscription.product.get()
              // );
              // subscription.price = removeTimestampCTorFromDocumentSnapshot<Price>(
              //   await firestoreSubscription.price.get()
              // );

              await new Promise(resolve => {
                let count = 0;

                firestoreSubscription.prices.forEach(async (item: DocumentReference<DocumentData>, index: number) => {
                  count++;
                  subscription.prices[index] = removeTimestampCTorFromDocumentSnapshot<Price>(await getDoc(item));
                  if (count === firestoreSubscription.prices.length) {
                    resolve(true);
                  }
                });
              });

              // const subscriptionPriceData = (await subscription.price.get()).data();
              //

              that.zone.run(() => {
                firestoreSubscription.items.forEach((item: SubscriptionItem, index: number) => {
                  subscription.items[index] = item;

                  that.store.dispatch(
                    addSubscriptionItemToAccount({
                      subscription: item
                    })
                  );
                });

                that.store.dispatch(
                  addSubscriptionToAccount({
                    subscription
                  })
                );
              });
            });
          });
        }
      );

      if (this.trialSub) {
        this.trialSub();
      }

      this.trialSub = onSnapshot(this.sptFirestore.docRef(firestoreTrial()), (r: DocumentSnapshot<DocumentData>) => {
        that.zone.run(() => {
          that.store.dispatch(addTrialToAccount(removeTimestampCTorFromDocumentSnapshot(r) as TrialModel));
        });
      });
    }

    this.sptFirestore.setWebSocketConnected(ACCOUNT_FEATURE_KEY);

    if (!this.accountChangesSub) {
      /**
       * ACCOUNT
       */
      this.accountChangesSub = onSnapshot(
        this.sptFirestore.docRef(getMusicianAccountPath(user)),
        (_doc: DocumentSnapshot<DocumentData>) => {
          const account: AccountState = removeTimestampCTorFromDocumentSnapshot<AccountState>(_doc);

          if (account && account.uid) {
            this.sptFirestore
              .upsertDoc(getMusicianPublicPathByUser(account.uid), <PublicProfile>{
                production: this.environment.production,
                stageName: account.stageName,
                photoURL: user.auth.photoURL,
                uid: user.auth.uid,
                displayName: user.auth.displayName
              })
              .subscribe(() => {
                /* noop */
              });
          }

          if (_doc.exists()) {
            that.zone.run(() => {
              that.store.dispatch(
                accountUpdated({
                  account
                })
              );
            });
          }

          // Only call if has email and stagenem
          if (
            _doc.exists() &&
            account &&
            account.stageName &&
            account.stageName.length &&
            !account.defaultProjectId &&
            !this.isCreatingDefaultEntities
          ) {
            this.isCreatingDefaultEntities = true;
            this.defaultIdsService.createDefaultEntities(account);
          }

          /**
           * NOTIFICATIONS
           */

          if (this.notificationsSub) {
            this.notificationsSub();
          }
          this.notificationsSub = onSnapshot(
            this.sptFirestore.collectionRef(firestoreGetMusicianNotificationsPath(user)),
            (_no: QuerySnapshot) => {
              this.processNotifications(_no.docChanges());
            }
          );

          /**
           * PROMO CODE
           */
          if (account.promoCode && account.promoCode.length) {
            if (this.promoCodeSub) {
              this.promoCodeSub();
            }

            this.promoCodeSub = onSnapshot(
              this.sptFirestore.docRef(firestorePromoCodeDict(account.promoCode)),
              (_snapshot: DocumentSnapshot<DocumentData>) => {
                that.zone.run(() => {
                  that.store.dispatch(
                    loadPromoCode({promoCode: removeTimestampCTorFromDocumentSnapshot(_snapshot) as PromoCodeDict})
                  );
                });
              }
            );
          }
        }
      );
    }
  }

  onDisconnect() {
    if (this.accountChangesSub) {
      this.accountChangesSub();
    }
    if (this.notificationsSub) {
      this.notificationsSub();
    }
    if (this.subscriptionsSub) {
      this.subscriptionsSub();
    }
    if (this.promoCodeSub) {
      this.promoCodeSub();
    }
    if (this.trialSub) {
      this.trialSub();
    }

    this.trackCloudStorage.disconnect();

    this.sptFirestore.setWebSocketDisconnected(ACCOUNT_FEATURE_KEY);
  }

  processNotifications(snapshot: DocumentChange[]) {
    const aggregate: AggregateFirebaseSnapshotChanges<ApiNotifiation> = snapshot.reduce(
      (acc: AggregateFirebaseSnapshotChanges<ApiNotifiation>, change: DocumentChange) => {
        // console.log(change.doc.ref.path);

        // const data: ApiNotifiation = change.doc.data() as ApiNotifiation;
        const data: ApiNotifiation = removeTimestampCTorFromDocumentSnapshot(change.doc);

        acc.all.push(data);

        if (data.type === NotificationTypes.ResetBeta) {
          this.showResetModal();
        }

        if (change.type === 'added') {
          // console.log('New Project: ', change.doc.data());
          acc.added.push(data);
        }

        if (change.type === 'modified') {
          // console.log('Modified Project: ', change.doc.data());
          // acc.modified.push(<UpdateStr<ApiNotifiation>>{
          //   id: data.id,
          //   changes: data,
          // });
        }

        if (change.type === 'removed') {
          // console.log('Removed Track: ', data);
          acc.removed.push(data.type);
        }

        return acc;
      },
      {
        added: [],
        modified: [],
        removed: [],
        all: []
      }
    );
  }

  showResetModal() {
    // const dialogRef = this.dialog.open(ResetEnvironmentComponent, {
    //   width: '600px',
    //   minHeight: '500px'
    // });
  }

  watchElectronLogin() {
    // console.log('watchElectronLogin');
    this.watchLoginSub.unsubscribe();

    this.watchLoginSub = this.store
      .pipe(
        select(selectAuthState),
        // tap((auth: AuthState) => {
        //   console.log(auth);
        // }),
        distinctUntilKeyChanged('isLoggedIn'),
        mergeMap((auth: AuthState) => {
          // console.log(account, auth);

          return this.store.pipe(
            select(deviceIsElectron),
            map((isElectron: boolean) => {
              return {
                auth,
                url: this.locationService.path(),
                isElectron
              };
            })
          );
        })
      )
      .subscribe(({auth, url, isElectron}: {auth: AuthState; url: string; isElectron: boolean}) => {
        // console.log('isLoggedIn', auth);
        // console.log('url', url);
        // console.log('isElectron', isElectron);
        // console.log('url === `/${this.logoutRedirect}`', url === `/${this.logoutRedirect}`);

        /**
         * If Logged in
         * AND on the Login Route
         * THEN Navigate to the counsolelibs/mixer-browser-desktop/data-access/src/lib/payment/reset-dev-environment.service.t
         */
        if (auth.isLoggedIn && url.includes(APP_ROUTES.LOGIN_ELECTRON)) {
          this.zone.run(() => {
            this.router.navigate([getRouteConfigLinkByName(APP_ROUTES.MIXER_CONSOLE)]).then(() => {
              /* noop */
            });
          });
        }

        /**
         * If NOT Logged in
         * AND NOT on the Login Route
         * THEN Navigate to login route
         */
        if (!auth.isLoggedIn && !url.includes(APP_ROUTES.LOGIN_ELECTRON)) {
          // console.log('NAVIGATE TO LOGIN');

          if (isElectron) {
            // libs/mixer-browser-desktop/feature-app/src/lib/api-options.ts
            this.zone.run(() => {
              this.router.navigate([this.logoutRedirect]);
            });

            timer(250)
              .pipe(
                switchMap(() => {
                  localStorage.clear();
                  return this.storage.clear();
                })
              )
              .subscribe(() => {
                location.reload();
              });
          }
        }
      });
  }
}
